import Api, { type FetchConfig } from '@op/opds.api-base';
import { logger } from './logging-api';
import { getCurrentLanguage } from '../i18n';
import { setErrorState } from 'individual/components/common/ErrorBoundary';

const api = new Api(window.__RHVKLSO002_CONFIG.LIFEPURCHASE_SERVICE_API_URL, {
  acceptLanguages: [getCurrentLanguage()],
});

/**
 * Make a GET request to the server
 * @param url
 * @param config
 */
export async function get<T>(url: string, config: FetchConfig): Promise<T> {
  return await handleResponse(
    url,
    api.get(url, { ...config, rawResponse: true })
  );
}

/**
 * Make a POST request to the server
 * @param url
 * @param body
 * @param config
 */
export async function post<T>(
  url: string,
  body: string | FormData | object,
  config?: FetchConfig
): Promise<T> {
  return await handleResponse(
    url,
    api.post(url, { ...config, body, rawResponse: true })
  );
}

/**
 * Make a PUT request to the server
 * @param url
 * @param body
 */
export async function put<T>(
  url: string,
  body: string | FormData | object
): Promise<T> {
  return await handleResponse(url, api.put(url, { body, rawResponse: true }));
}

/**
 * Make a DELETE request to the server
 * @param url
 * @param pathParams
 */
export async function del<T>(
  url: string,
  pathParams: Record<string, string | number> = {}
): Promise<T> {
  return await handleResponse(
    url,
    api.delete(url, { pathParams, rawResponse: true })
  );
}

/**
 * Handle server response. If request succeeds but the response has success: false
 * then logs a ServerError and sets the application ErrorState to true
 * @private
 * @async
 * @param request
 */
export async function handleResponse(url: string, request: Promise<any>) {
  try {
    const res = await request;
    const contentType = res?.headers?.get('content-type');
    if (contentType?.indexOf('application/json') === -1) {
      setErrorState(true);
    }
    return res.json();
  } catch (error: any) {
    if (error.name === 'AbortError') {
      // Requests aborted manually with AbortController should not cause an error
      console.debug('Request aborted');
    } else {
      logger.logErrorFromRequest(error as Error, url);
      setErrorState(true);
    }
  }
}

/* eslint-disable promise/avoid-new */
const delay = async (ms: number): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, ms));
};
/* eslint-enable promise/avoid-new */

interface GetWithRetryType {
  url: string;
  config: FetchConfig;
  retries?: number;
  delayMs?: number;
  backoff?: number;
}

export async function getWithRetry({
  url,
  config,
  retries = 3,
  backoff = 2,
  delayMs = 1000,
}: GetWithRetryType) {
  let attempts = 0;

  while (attempts < retries) {
    try {
      const response = await api.get(url, { ...config, rawResponse: true });
      const contentType = response?.headers?.get('content-type');

      if (!response.ok || contentType?.indexOf('application/json') === -1) {
        throw new Error(`HTTP error.`);
      }
      return response.json();
    } catch (error) {
      attempts++;
      if (attempts >= retries) {
        const errorObj = error as Error;
        errorObj.message = `Failed to fetch product documents - ${errorObj.message}`;
        logger.logError(errorObj);
        setErrorState(true);
      }
      await delay(delayMs * Math.pow(backoff, attempts - 1));
    }
  }
}
